/* eslint-disable @typescript-eslint/naming-convention */
/**
 * It uses `_` instead of `-` due to JS syntax.
 *
 * Right part of Enum should be canonical Locale (lowercase_UPPERCASE).
 * Right part should always have a country part (so, "Locale" by our definition, not "Language").
 */
export var LANG = /*#__PURE__*/function (LANG) {
  LANG["en_US"] = "en-US";
  LANG["en_GB"] = "en-GB";
  LANG["es_US"] = "es-US";
  LANG["pt_BR"] = "pt-BR";
  LANG["sv_SE"] = "sv-SE";
  LANG["en_CA"] = "en-CA";
  // These languages were removed in 2019:
  // de = 'de',
  // fi = 'fi',
  // da = 'da',
  // no = 'no',
  return LANG;
}(LANG || {});
/* eslint-enable @typescript-eslint/naming-convention */
export const LANG_DEFAULT = LANG.en_US;
/**
 * These are all the languages that we can have Airtable translations in
 */
export const TRANSLATED_LANGUAGES = [LANG.en_US, LANG.en_GB, LANG.es_US, LANG.pt_BR, LANG.sv_SE, LANG.en_CA];
/**
 * These are the languages supported in the app
 *
 * Please be careful with the order.
 * Order is used to define "default Locale per Language", e.g `en-US` locale is default for `en` language, so it should be above other en-* locales (such as `en-GB`).
 */
export const SUPPORTED_LANGUAGES = TRANSLATED_LANGUAGES.filter(lang => lang !== LANG.en_CA);
export const DEPRECATED_LANGUAGES = [];
const ALL_LANGUAGES = [...SUPPORTED_LANGUAGES, ...DEPRECATED_LANGUAGES];
/**
 * undefined > undefined
 * en > en
 * en-us > en-US
 * en-US > en-US
 * sv_SE > sv-SE
 * en_U > en
 *
 * https://tools.ietf.org/html/bcp47
 */
export function normalizeLanguage(lang) {
  if (!lang || lang.length < 2) return undefined;
  const langPart = lang.slice(0, 2).toLowerCase();
  if (lang.length < 5) {
    // auto-corrects e.g. `en-U` into `en`
    return langPart;
  }
  const countryPart = lang.slice(3, 5).toUpperCase();
  return [langPart, countryPart].join('-');
}
/**
 * Converts any input language string to supported LANG.
 * Never returns undefined (uses LANG_DEFAULT).
 * Supports wrongly lowercased/uppercased input.
 * Maps languages as needed.
 *
 * Does NOT do validation (should be done earlier), so it just takes first 2 chars and chars 4 and 5 into consideration, ignoring anything else.
 * This means that it's "forgiving", so e.g `en_US` will work (despite that `_` is used instead of `-`).
 */
export function toSupportedLanguage(lang, allowDeprecated = false) {
  return toSupportedLanguageOrUndefined(lang, allowDeprecated) || LANG_DEFAULT;
}
/**
 * Converts any input language string to LANG we have translations for.
 * @see toSupportedLanguage
 */
export function toTranslatedLanguage(lang) {
  return toTranslatedLanguageOrUndefined(lang) || LANG_DEFAULT;
}
function getLangStringParts(lang) {
  const langPart = lang.slice(0, 2).toLowerCase();
  const countryPart = lang.slice(3, 5).toUpperCase();
  return [langPart, countryPart];
}
function getMatchingLangOrUndefined(langPart, countryPart, langs) {
  // to canonical format
  const canonical = [langPart, countryPart].join('-');
  if (langs.includes(canonical)) return canonical;
  return langs.find(supportedLang => supportedLang.startsWith(langPart));
}
export function toSupportedLanguageOrUndefined(lang, allowDeprecated = false) {
  if (!lang || lang.length < 2) return;
  const [langPart, countryPart] = getLangStringParts(lang);
  const LANGUAGES = allowDeprecated ? ALL_LANGUAGES : SUPPORTED_LANGUAGES;
  return getMatchingLangOrUndefined(langPart, countryPart, LANGUAGES);
}
export function toTranslatedLanguageOrUndefined(lang) {
  if (!lang || lang.length < 2) return;
  const [langPart, countryPart] = getLangStringParts(lang);
  return getMatchingLangOrUndefined(langPart, countryPart, TRANSLATED_LANGUAGES);
}
/**
 * urlPath should start with `/` (if it does).
 * urlPath is what browser returns on `location.pathname`
 * Can be undefined, empty string, etc.
 *
 * Function is "forgiving", will only use a substring between first and next `/` character (if found)
 */
export function urlPathToSupportedLanguage(urlPath = '', allowDeprecated = false) {
  const firstSlashIndex = urlPath.indexOf('/');
  const secondSlashIndex = urlPath.indexOf('/', firstSlashIndex + 1);
  const langFragment = urlPath.substring(firstSlashIndex + 1, secondSlashIndex !== -1 ? secondSlashIndex : urlPath.length);
  return toSupportedLanguage(langFragment, allowDeprecated);
}
export const DAYJS_LOCALE_MAP = {
  [LANG.en_US]: 'en',
  [LANG.en_GB]: 'en-gb',
  [LANG.es_US]: 'es-us',
  [LANG.pt_BR]: 'pt-br',
  [LANG.sv_SE]: 'sv',
  [LANG.en_CA]: 'en-ca'
};